import React, { useCallback, useState, useEffect, useRef } from 'react';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import CircleArrow from 'components/Core/Carousel/images/CircleArrow';
import { STIcon } from 'components/Brand/Icons/STIcon';
import { ArrowContainer, SliderContainer, HeaderItems } from './styles';

const CustomCarouselPaging = ({
  versionStyling,
  sliderRef,
  contentful_id: contentfulID,
  currentPage,
  headerItems,
  infinite,
}) => {
  const [ArrowPositioning, setArrowPositioning] = useState(null);
  const hasPrev = infinite || currentPage !== 0;
  const hasNext =
    infinite ||
    !sliderRef?.current ||
    currentPage + sliderRef?.current?.props.slidesToShow <
      sliderRef?.current?.props?.children.length;
  const headerItemsRef = useRef();

  const sliderDataLayerPush = (sliderButton) => {
    dataLayer.push({
      event: 'cta clicked',
      cta_text: sliderButton,
      cta_position: 'sliderengaged',
    });
  };

  const handleArrowPositioning = useCallback(() => {
    if (sliderRef?.current) {
      const { currentSlide } = sliderRef.current.innerSlider.state;
      const section = document.getElementById(`section-${contentfulID}`);
      if (section) {
        const getArrowPosition = (element, offset) => {
          if (element) {
            setArrowPositioning(
              element.getBoundingClientRect().top - section.getBoundingClientRect().top + offset,
            );
          }
        };

        if (versionStyling === 'V3') {
          const copy = section
            .querySelector(`[data-index='${currentSlide}']`)
            ?.querySelectorAll('.column-root')[1];
          getArrowPosition(copy, -6);
        }

        if (['V2', 'V5'].includes(versionStyling)) {
          const copy = section
            .querySelector(`[data-index='${currentSlide}']`)
            ?.querySelectorAll('.column-root')[4]?.firstChild;
          getArrowPosition(copy, 40);
        }
      }
    }
  }, [contentfulID, versionStyling]);

  useEffect(() => {
    const section = document.getElementById(`section-${contentfulID}`);
    let resizeObserver = null;
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        handleArrowPositioning();
      });
    }
    if (section) {
      resizeObserver?.observe(section);
    }
    return () => {
      if (section) {
        resizeObserver?.unobserve(section);
      }
    };
  }, [handleArrowPositioning, contentfulID]);

  useEffect(() => {
    const section = document.getElementById(`section-${contentfulID}`);
    const mutationObserver = new MutationObserver(() => {
      handleArrowPositioning();
    });
    if (section) {
      mutationObserver.observe(section, { childList: true, subtree: true });
    }
    return () => {
      if (section) {
        mutationObserver.disconnect();
      }
    };
  }, [handleArrowPositioning, contentfulID]);

  useEffect(() => {
    handleArrowPositioning();
    window.addEventListener('resize', handleArrowPositioning);
    return () => {
      window.removeEventListener('resize', handleArrowPositioning);
    };
  }, [handleArrowPositioning]);

  const scrollToHeaderItem = (item) => {
    const { left } = item.getBoundingClientRect();

    if (typeof headerItemsRef?.current?.scrollBy === 'function') {
      headerItemsRef.current.scrollBy({
        left: left - 20,
        behavior: 'smooth',
      });
    }
  };

  if (currentPage !== undefined && typeof window !== 'undefined') {
    const item = document.querySelector(
      `.header-items > p:nth-child(${parseInt(currentPage) + 1})`,
    );

    if (item) {
      scrollToHeaderItem(item);
    }
  }

  const onPrev = () => {
    sliderDataLayerPush('previous slide');
    if (hasPrev) {
      sliderRef?.current?.slickPrev();
    }
  };

  const onNext = () => {
    sliderDataLayerPush('next slide');
    if (hasNext) {
      sliderRef?.current?.slickNext();
    }
  };

  const goTo = (index, e) => {
    sliderDataLayerPush(`slide ${index} selected`);
    sliderRef?.current?.slickGoTo(index);
    scrollToHeaderItem(e.target);
  };

  const handleTouchStart = useCallback((event) => {
    event.currentTarget.classList.add('active');
  }, []);

  const handleTouchEnd = useCallback((event) => {
    event.currentTarget.classList.remove('active');
    event.currentTarget.blur();
  }, []);

  return (
    <SliderContainer
      data-testid="slider-container"
      versionStyling={versionStyling}
      top={ArrowPositioning}
    >
      <ArrowContainer
        data-testid="arrow-container"
        className="arrow-container"
        versionStyling={versionStyling}
      >
        {versionStyling === 'Bender' ? (
          <>
            <button
              aria-label="previous"
              className="left-arrow"
              type="button"
              onClick={onPrev}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <ArrowBackIosNewIcon />
            </button>
            <button
              aria-label="next"
              className="right-arrow"
              type="button"
              onClick={onNext}
              onTouchStart={handleTouchStart}
              onTouchEnd={handleTouchEnd}
            >
              <ArrowForwardIosIcon />
            </button>
          </>
        ) : versionStyling !== 'V5' ? (
          <>
            <CircleArrow onClick={onPrev} />
            <CircleArrow onClick={onNext} />
          </>
        ) : (
          versionStyling === 'V5' && (
            <>
              <STIcon
                type="left-arrow-circle"
                className={hasPrev ? '' : 'disabled'}
                onClick={onPrev}
              />
              <STIcon
                type="left-arrow-circle"
                className={hasNext ? '' : 'disabled'}
                onClick={onNext}
              />
            </>
          )
        )}
      </ArrowContainer>
      {versionStyling === 'V5' && headerItems && (
        <HeaderItems className="header-items" ref={headerItemsRef}>
          {headerItems.map((item, index) => (
            <p
              className={currentPage === index ? 'selected' : ''}
              onClick={(e) => goTo(index, e)}
              key={index}
            >
              {item}
            </p>
          ))}
        </HeaderItems>
      )}
    </SliderContainer>
  );
};

export default CustomCarouselPaging;
